import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Selector de fecha estilo Carbon (DatePicker simple). Valor en 'YYYY-MM-DD'
// o '' sin fecha. El calendario va en un portal para que el overflow del
// modal no lo recorte.

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DIAS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const aTexto = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const deTexto = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const hoy = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};
export const fechaLarga = (s) => deTexto(s).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });

const ANCHO = 296;
const ALTO = 360;

export const SelectorFecha = ({ value, onChange, placeholder = 'Selecciona una fecha', minHoy = false }) => {
  const [abierto, setAbierto] = useState(false);
  const [vista, setVista] = useState(() => (value ? deTexto(value) : hoy()));
  const [pos, setPos] = useState(null);
  const campoRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => { if (value) setVista(deTexto(value)); }, [value]);

  // Debajo del campo; si no cabe, encima.
  useLayoutEffect(() => {
    if (!abierto || !campoRef.current) return;
    const ubicar = () => {
      const r = campoRef.current.getBoundingClientRect();
      const arriba = r.bottom + ALTO > window.innerHeight && r.top > ALTO;
      setPos({
        left: Math.min(r.left, window.innerWidth - ANCHO - 8),
        top: arriba ? r.top - ALTO - 4 : r.bottom + 4
      });
    };
    ubicar();
    window.addEventListener('resize', ubicar);
    window.addEventListener('scroll', ubicar, true);
    return () => {
      window.removeEventListener('resize', ubicar);
      window.removeEventListener('scroll', ubicar, true);
    };
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e) => {
      if (!campoRef.current?.contains(e.target) && !popRef.current?.contains(e.target)) setAbierto(false);
    };
    const tecla = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setAbierto(false); } };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', tecla, true);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', tecla, true);
    };
  }, [abierto]);

  const anio = vista.getFullYear();
  const mes = vista.getMonth();
  const primero = (new Date(anio, mes, 1).getDay() + 6) % 7; // lunes = 0
  const celdas = Array.from({ length: 42 }, (_, i) => new Date(anio, mes, i - primero + 1));
  const hoyTxt = aTexto(hoy());
  const limite = minHoy ? hoy() : null;
  const irMes = (delta) => setVista(new Date(anio, mes + delta, 1));
  const elegir = (d) => {
    onChange(aTexto(d));
    setAbierto(false);
  };

  return (
    <>
      <div ref={campoRef} className={`fecha-campo ${abierto ? 'abierto' : ''}`}>
        <button type="button" className="fecha-campo-btn" onClick={() => setAbierto((a) => !a)} aria-haspopup="dialog" aria-expanded={abierto}>
          <span className={value ? '' : 'fecha-campo-vacio'}>{value ? fechaLarga(value) : placeholder}</span>
          <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
            <path d="M26 4h-4V2h-2v2h-8V2h-2v2H6a2 2 0 0 0-2 2v20a2 2 0 0 0 2 2h20a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 22H6V12h20Zm0-16H6V6h4v2h2V6h8v2h2V6h4Z" />
          </svg>
        </button>
        {value && (
          <button type="button" className="fecha-campo-limpiar" onClick={() => onChange('')} aria-label="Quitar fecha" title="Quitar fecha">
            <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
              <path d="M24 9.4 22.6 8 16 14.6 9.4 8 8 9.4l6.6 6.6L8 22.6 9.4 24l6.6-6.6 6.6 6.6 1.4-1.4-6.6-6.6L24 9.4z" />
            </svg>
          </button>
        )}
      </div>
      {abierto && pos && createPortal(
        <div ref={popRef} className="fecha-pop" style={{ left: pos.left, top: pos.top, width: ANCHO }} role="dialog" aria-label="Calendario">
          <div className="fecha-pop-cab">
            <button type="button" onClick={() => irMes(-1)} aria-label="Mes anterior">
              <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M10 16 20 6l1.4 1.4-8.6 8.6 8.6 8.6L20 26z" /></svg>
            </button>
            <span><strong>{MESES[mes]}</strong> {anio}</span>
            <button type="button" onClick={() => irMes(1)} aria-label="Mes siguiente">
              <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M22 16 12 26l-1.4-1.4 8.6-8.6-8.6-8.6L12 6z" /></svg>
            </button>
          </div>
          <div className="fecha-pop-grid">
            {DIAS.map((d, i) => <span key={i} className="fecha-pop-dia">{d}</span>)}
            {celdas.map((d) => {
              const txt = aTexto(d);
              const fuera = d.getMonth() !== mes;
              const bloqueado = limite && d < limite;
              return (
                <button
                  type="button"
                  key={txt}
                  className={[
                    'fecha-pop-celda',
                    fuera && 'fuera',
                    txt === hoyTxt && 'hoy',
                    txt === value && 'sel'
                  ].filter(Boolean).join(' ')}
                  disabled={bloqueado}
                  onClick={() => elegir(d)}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
          <div className="fecha-pop-pie">
            <button type="button" onClick={() => setVista(hoy())}>Ir a hoy</button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
